import ExternalLink from "@/components/ExternalLink";
import { BRAND_COLORS } from "@/lib/brandColors";
import { Ionicons } from "@expo/vector-icons";
import React from "react";
import { Platform, Pressable, Text } from "react-native";
import { XStack, YStack } from "tamagui";

export type AlertStripTone = "warning" | "danger" | "info";

const ALERT: Record<
  AlertStripTone,
  {
    bg: string;
    border: string;
    text: string;
    icon: keyof typeof Ionicons.glyphMap;
    iconColor: string;
    cta: string;
  }
> = {
  warning: {
    bg: "#FFF6E8",
    border: "rgba(152, 116, 81, 0.25)",
    text: "#5C4A32",
    icon: "card-outline",
    iconColor: BRAND_COLORS.instructorMeta,
    cta: BRAND_COLORS.linkAccent,
  },
  danger: {
    bg: BRAND_COLORS.errorBg,
    border: "rgba(229, 38, 0, 0.18)",
    text: "#7A1F0E",
    icon: "alert-circle-outline",
    iconColor: BRAND_COLORS.error,
    cta: BRAND_COLORS.error,
  },
  info: {
    bg: "#F4F1ED",
    border: BRAND_COLORS.border,
    text: BRAND_COLORS.text,
    icon: "time-outline",
    iconColor: BRAND_COLORS.muted,
    cta: BRAND_COLORS.linkAccent,
  },
};

export type AppAlertStripProps = {
  tone: AlertStripTone;
  message: string;
  href?: string;
  actionLabel?: string;
  /** WP-style “Remind me later” (optional). */
  onRemindLater?: () => void;
  /** WP-style dismiss X (optional). */
  onDismiss?: () => void;
};

/** Full-bleed alert strip — sits under the app header (not in the notch). */
export function AppAlertStrip({
  tone,
  message,
  href,
  actionLabel = "Manage",
  onRemindLater,
  onDismiss,
}: AppAlertStripProps) {
  const a = ALERT[tone];
  const hasActions = Boolean(href || onRemindLater || onDismiss);

  const primaryButton = href ? (
    <ExternalLink url={href}>
      <YStack
        backgroundColor={tone === "info" ? BRAND_COLORS.text : BRAND_COLORS.accent}
        px={12}
        py={7}
        borderRadius={6}
        {...(Platform.OS === "web" ? ({ cursor: "pointer" } as object) : {})}
      >
        <Text
          style={{
            color: "#FFF",
            fontSize: 12,
            lineHeight: 16,
            fontWeight: "700",
            fontFamily: "Inter",
          }}
        >
          {actionLabel}
        </Text>
      </YStack>
    </ExternalLink>
  ) : null;

  const remindButton = onRemindLater ? (
    <Pressable
      onPress={onRemindLater}
      accessibilityRole="button"
      accessibilityLabel="Remind me later"
      style={
        Platform.OS === "web" ? ({ cursor: "pointer" } as object) : undefined
      }
    >
      <YStack
        backgroundColor="#FFF"
        borderWidth={1}
        borderColor={a.cta}
        px={12}
        py={6}
        borderRadius={6}
      >
        <Text
          style={{
            color: a.cta,
            fontSize: 12,
            lineHeight: 16,
            fontWeight: "600",
            fontFamily: "Inter",
          }}
        >
          Remind me later
        </Text>
      </YStack>
    </Pressable>
  ) : null;

  const dismissButton = onDismiss ? (
    <Pressable
      onPress={onDismiss}
      accessibilityRole="button"
      accessibilityLabel="Dismiss"
      hitSlop={10}
      style={
        Platform.OS === "web" ? ({ cursor: "pointer" } as object) : undefined
      }
    >
      <Ionicons name="close" size={18} color={a.text} />
    </Pressable>
  ) : null;

  return (
    <XStack
      w="100%"
      px={14}
      py={6}
      ai="center"
      jc="center"
      gap={6}
      flexWrap="wrap"
      backgroundColor={a.bg}
      borderBottomWidth={1}
      borderBottomColor={a.border}
    >
      <XStack ai="flex-start" gap={8} maxWidth={hasActions ? 520 : 720} flexShrink={1}>
        <YStack flexShrink={0} pt={1}>
          <Ionicons name={a.icon} size={16} color={a.iconColor} />
        </YStack>
        <Text
          style={{
            flexShrink: 1,
            textAlign: "left",
            color: a.text,
            fontSize: 12,
            lineHeight: 18,
            fontWeight: "500",
            fontFamily: "Inter",
          }}
        >
          {message}
        </Text>
      </XStack>

      {hasActions ? (
        <XStack ai="center" gap={8} flexWrap="wrap" jc="center">
          {primaryButton}
          {remindButton}
          {dismissButton}
        </XStack>
      ) : null}
    </XStack>
  );
}
